import type { DiaSemana,PadraoRecorrencia } from '@features/tarefas/utils/tarefasUtils';

let __cacheSvc: any;
async function getCacheService() {
  if (!__cacheSvc) {
    const mod = await import('./cacheService');
    __cacheSvc = mod.cacheService;
    try { await __cacheSvc.init?.(); } catch {}
  }
  return __cacheSvc;
}
import { supabase } from './supabaseClient';
// ✅ IMPORTAR novos tipos para recorrência e subetapas

export interface OfflineAction {
  id: string;
  type: 'CREATE' | 'UPDATE' | 'DELETE';
  table: string;
  data: any;
  originalId?: string;
  timestamp: number;
  retries: number;
  status: 'pending' | 'syncing' | 'synced' | 'failed';
}

class OfflineSyncService {
  private syncQueue: OfflineAction[] = [];
  private isSyncing = false;
  private isReady = false; // ✅ NOVO: Flag para indicar que a fila foi carregada
  private readyPromise: Promise<void>; // ✅ NOVO: Promise para aguardar carregamento
  private maxRetries = 5; // ✅ Aumentado para dar mais chances com backoff
  private baseDelayMs = 1500; // ✅ Delay base para backoff exponencial (1.5s)
  private syncInterval: NodeJS.Timeout | null = null;
  private listeners: Array<(status: any) => void> = [];

  constructor() {
    // ✅ CORREÇÃO: Armazenar promise para aguardar carregamento
    this.readyPromise = this.loadSyncQueue().then(() => {
      this.isReady = true;
      console.log('✅ ACOR Sync: Fila carregada e pronta');
    });

    this.startSyncMonitor();

    // Listener para mudanças de conectividade
    window.addEventListener('online', async () => {
      console.log('🔄 ACOR Sync: Conexão restaurada, aguardando fila carregar...');
      // ✅ CORREÇÃO: Aguardar fila estar pronta antes de sincronizar
      await this.readyPromise;
      console.log(`🔄 ACOR Sync: Fila pronta com ${this.syncQueue.length} itens, iniciando sincronização...`);
      this.syncAll();
    });

    window.addEventListener('beforeunload', () => {
      // A fila é persistida automaticamente no IndexedDB
    });
  }

  // MÉTODO PARA ADICIONAR LISTENERS DE STATUS
  addStatusListener(callback: (status: any) => void) {
    this.listeners.push(callback);
  }

  removeStatusListener(callback: (status: any) => void) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  private notifyListeners() {
    const status = this.getSyncStatus();
    this.listeners.forEach(listener => listener(status));
  }

  // Carregar fila do IndexedDB (migrando do localStorage se existir)
  private async loadSyncQueue() {
    try {
      const toMigrate = localStorage.getItem('acor-sync-queue');
      if (toMigrate) {
        const list = JSON.parse(toMigrate) as OfflineAction[];
        const cs = await getCacheService();
        for (const a of list) {
          await cs.addToSyncQueue({
            type: a.type.toLowerCase() as any,
            table: a.table,
            data: a.data,
            id: a.originalId
          });
        }
        localStorage.removeItem('acor-sync-queue');
        console.log(`🛫 ACOR Sync: Migradas ${list.length} ações -> IndexedDB`);
      }
      // Agora fonte da verdade é IndexedDB:
      const cs = await getCacheService();
      this.syncQueue = (await cs.getSyncQueue()).map((row: any) => ({
        id: String(row.id),
        type: row.type.toUpperCase(),
        table: row.table,
        data: row.data,
        originalId: row.id || row.data?.id,
        timestamp: row.timestamp,
        retries: row.retries ?? 0,
        status: row.synced ? 'synced' : 'pending'
      }));
      console.log(`📋 ACOR Sync: ${this.syncQueue.length} ações carregadas do IndexedDB`);
    } catch (e) {
      console.error('❌ ACOR Sync: Erro ao carregar fila do IndexedDB:', e);
      this.syncQueue = [];
    }
  }


  // Monitorar e sincronizar automaticamente
  private startSyncMonitor() {
    // Verificar a cada 30 segundos se está online e há itens pendentes
    this.syncInterval = setInterval(async () => {
      // ✅ CORREÇÃO: Só sincronizar se a fila estiver pronta
      if (!this.isReady) return;
      if (navigator.onLine && this.syncQueue.length > 0 && !this.isSyncing) {
        console.log(`🔄 ACOR Sync: Monitor detectou ${this.syncQueue.length} itens pendentes, iniciando sincronização...`);
        this.syncAll();
      }
    }, 30000);
  }

  // MÉTODO PÚBLICO PARA ADICIONAR À FILA
  async addToQueue(action: Omit<OfflineAction, 'id' | 'timestamp' | 'retries' | 'status'>): Promise<string> {
    // ✅ CORREÇÃO: Aguardar fila estar pronta antes de adicionar
    await this.readyPromise;

    // Adicionar ao IndexedDB primeiro
    const cs = await getCacheService();
    const syncId = await cs.addToSyncQueue({
      type: action.type.toLowerCase() as any,
      table: action.table,
      data: action.data,
      id: action.originalId
    });

    const offlineAction: OfflineAction = {
      ...action,
      id: String(syncId),
      timestamp: Date.now(),
      retries: 0,
      status: 'pending'
    };

    this.syncQueue.push(offlineAction);

    console.log(`➕ ACOR Sync: Ação ${action.type} adicionada à fila (${action.table}) - ID: ${syncId}`);

    // Tentar sincronizar imediatamente se online
    if (navigator.onLine) {
      this.syncAll();
    }

    return offlineAction.id;
  }

  // Sincronizar todas as ações pendentes
  async syncAll(): Promise<void> {
    // ✅ CORREÇÃO: Aguardar fila estar pronta
    await this.readyPromise;

    if (this.isSyncing || !navigator.onLine) {
      return;
    }

    if (this.syncQueue.length === 0) {
      console.log('✅ ACOR Sync: Fila vazia, nada para sincronizar');
      this.notifyListeners(); // ✅ Notificar que a fila está vazia
      return;
    }

    this.isSyncing = true;
    console.log(`🔄 ACOR Sync: Iniciando sincronização de ${this.syncQueue.length} ações...`);

    const pendingActions = this.syncQueue.filter(action => action.status === 'pending');
    let successCount = 0;
    let failureCount = 0;

    for (const action of pendingActions) {
      try {
        action.status = 'syncing';
        await this.syncSingleAction(action);
        action.status = 'synced';
        successCount++;
        // marca no IndexedDB
        const cs = await getCacheService();
        await cs.markSynced(Number(action.id));
        
        console.log(`✅ ACOR Sync: ${action.type} ${action.table} sincronizado`);
        
      } catch (error) {
        console.error(`❌ ACOR Sync: Erro ao sincronizar ${action.type} ${action.table}:`, error);
        
        action.retries++;
        const willFail = action.retries >= this.maxRetries;
        action.status = willFail ? 'failed' : 'pending';
        if (!willFail) {
          // ✅ Backoff exponencial + jitter para evitar "martelar" o servidor
          const delay = Math.min(15000, this.baseDelayMs * 2 ** (action.retries - 1));
          const jitter = Math.floor(Math.random() * 500);
          const totalDelay = delay + jitter;
          console.log(`⏳ ACOR Sync: Retry ${action.retries}/${this.maxRetries} em ${totalDelay}ms (base: ${delay}ms + jitter: ${jitter}ms)`);
          await new Promise(r => setTimeout(r, totalDelay));
        }
        failureCount++;
      }
    }

    // Remover ações sincronizadas com sucesso
    this.syncQueue = this.syncQueue.filter(action => action.status !== 'synced');

    console.log(`🏁 ACOR Sync: Concluído - ${successCount} sucesso, ${failureCount} falhas`);

    // Notificar usuário sobre resultados
    if (successCount > 0) {
      this.showSyncNotification(`${successCount} item(ns) sincronizado(s) com sucesso`);
    }

    if (failureCount > 0) {
      this.showSyncNotification(`${failureCount} item(ns) falharam na sincronização`);
    }

    this.isSyncing = false;
    this.notifyListeners();
  }

  // Sincronizar uma ação específica
  private async syncSingleAction(action: OfflineAction): Promise<void> {
    switch (action.type) {
      case 'CREATE':
        return this.syncCreate(action);
      case 'UPDATE':
        return this.syncUpdate(action);
      case 'DELETE':
        return this.syncDelete(action);
      default:
        throw new Error(`Tipo de ação não suportado: ${action.type}`);
    }
  }

  // ✅ ATUALIZADA: Sincronizar criação COM NOVOS CAMPOS
  private async syncCreate(action: OfflineAction): Promise<void> {
    const { table, data } = action;
    
    // ✅ ATUALIZADA: Limpar dados temporários incluindo campos de recorrência
    const cleanData = this.cleanDataForSync(data, 'CREATE');

    const { data: result, error } = await supabase
      .from(table)
      .insert(cleanData)
      .select()
      .single();

    if (error) throw error;

    // Atualizar cache local com ID real
    if (result && action.originalId) {
      await this.updateLocalCache(table, action.originalId, result);
    }
  }

  // ✅ ATUALIZADA: Sincronizar atualização COM NOVOS CAMPOS
  private async syncUpdate(action: OfflineAction): Promise<void> {
    const { table, data, originalId } = action;
    
    if (!originalId) {
      throw new Error('ID original necessário para UPDATE');
    }

    // ✅ ATUALIZADA: Limpar dados para update incluindo validação de recorrência
    const cleanData = this.cleanDataForSync(data, 'UPDATE');

    const { error } = await supabase
      .from(table)
      .update(cleanData)
      .eq('id', originalId);

    if (error) throw error;
  }

  // Sincronizar exclusão (mantida igual)
  private async syncDelete(action: OfflineAction): Promise<void> {
    const { table, originalId } = action;
    
    if (!originalId) {
      throw new Error('ID original necessário para DELETE');
    }

    const { error } = await supabase
      .from(table)
      .delete()
      .eq('id', originalId);

    if (error) throw error;
  }

  // ✅ NOVA: Função para limpar dados antes da sincronização
  private cleanDataForSync(data: any, operation: 'CREATE' | 'UPDATE'): any {
    const cleanData = { ...data };
    
    // ✅ Remover campos temporários/locais padrão
    delete cleanData.temp_id;
    delete cleanData.offline_created;
    delete cleanData.cached_at;
    delete cleanData._isVirtualTask;

    // ✅ NOVOS: Limpar campos específicos para diferentes operações
    if (operation === 'CREATE') {
      // ✅ Para criação, garantir que campos de recorrência estão consistentes
      if (cleanData.recorrente === false || !cleanData.recorrente) {
        // Se não é recorrente, limpar todos os campos relacionados
        cleanData.recorrente = false;
        cleanData.padrao_recorrencia = null;
        cleanData.intervalo_recorrencia = null;
        cleanData.dias_semana = null;
        cleanData.data_limite_recorrencia = null;
        cleanData.recorrencia_ativa = false;
        cleanData.proxima_ocorrencia = null;
        // Manter tarefa_pai_id se existir (para instâncias)
      } else if (cleanData.recorrente === true) {
        // Se é recorrente, garantir valores padrão
        cleanData.recorrencia_ativa = cleanData.recorrencia_ativa !== false;
        cleanData.intervalo_recorrencia = cleanData.intervalo_recorrencia || 1;
        
        // ✅ Validar padrão de recorrência
        const padraoValido: PadraoRecorrencia[] = ['daily', 'weekly', 'monthly', 'yearly', 'custom'];
        if (!padraoValido.includes(cleanData.padrao_recorrencia)) {
          cleanData.padrao_recorrencia = 'daily'; // Fallback
        }
        
        // ✅ Validar dias da semana se for semanal
        if (cleanData.padrao_recorrencia === 'weekly' && cleanData.dias_semana) {
          const diasValidos: DiaSemana[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
          cleanData.dias_semana = cleanData.dias_semana.filter((dia: string) => 
            diasValidos.includes(dia as DiaSemana)
          );
          if (cleanData.dias_semana.length === 0) {
            cleanData.dias_semana = ['monday']; // Fallback
          }
        }
      }
    }

    // ✅ Validar subetapa_id
    if (cleanData.subetapa_id === '' || cleanData.subetapa_id === undefined) {
      cleanData.subetapa_id = null;
    }

    // ✅ Validar campos de data
    const camposData = ['data_inicio', 'data_limite', 'data_conclusao', 'data_limite_recorrencia', 'proxima_ocorrencia'];
    camposData.forEach(campo => {
      if (cleanData[campo] === '' || cleanData[campo] === undefined) {
        cleanData[campo] = null;
      }
    });

    // ✅ Validar progresso
    if (cleanData.progresso_pct !== undefined) {
      const progresso = parseInt(String(cleanData.progresso_pct));
      if (isNaN(progresso) || progresso < 0 || progresso > 100) {
        cleanData.progresso_pct = 0;
      } else {
        cleanData.progresso_pct = progresso;
      }
    }

    // ✅ Validar multiplos_responsaveis
    if (cleanData.responsaveis_ids && Array.isArray(cleanData.responsaveis_ids)) {
      cleanData.multiplos_responsaveis = cleanData.responsaveis_ids.length > 1;
    }

    console.log(`🧹 ACOR Sync: Dados limpos para ${operation}:`, {
      recorrente: cleanData.recorrente,
      subetapa_id: cleanData.subetapa_id,
      padrao_recorrencia: cleanData.padrao_recorrencia
    });

    return cleanData;
  }

  // ✅ ATUALIZADA: Atualizar cache local com suporte a subetapas
  private async updateLocalCache(table: string, tempId: string, realData: any): Promise<void> {
    try {
      const cs = await getCacheService();
      if (table === 'tarefas') {
        // ✅ Atualizar tarefa no cache com dados reais incluindo subetapas
        await cs.updateTarefaFromSync(tempId, realData);
        
        // ✅ NOVO: Se a tarefa tem subetapa, garantir que está no cache
        if (realData.subetapa_id && realData.subetapas) {
          await cs.cacheSubEtapas([realData.subetapas]);
        }
      }
      
      // ✅ NOVO: Suporte para outras tabelas relacionadas
      if (table === 'subetapas') {
        await cs.cacheSubEtapas([realData]);
      }
      
      if (table === 'tarefas_responsaveis') {
        await cs.cacheTarefasResponsaveis([realData]);
      }
      
      if (table === 'grupos_tarefas') {
        await cs.cacheGruposTarefas([realData]);
      }
      
    } catch (error) {
      console.error('Erro ao atualizar cache local:', error);
    }
  }

  // Gerar ID único para ações offline
  private generateId(): string {
    return `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Mostrar notificação de sincronização
  private showSyncNotification(message: string) {
    // Implementar notificação visual ou usar service de notificação
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification('ACOR Sync', {
        body: message,
        icon: '/ERP-Acor/pwa-192x192.png',
        tag: 'sync-result'
      });

      setTimeout(() => notification.close(), 3000);
    }
  }

  // Obter status da fila de sincronização
  getSyncStatus() {
    const pending = this.syncQueue.filter(a => a.status === 'pending').length;
    const failed = this.syncQueue.filter(a => a.status === 'failed').length;
    const syncing = this.syncQueue.filter(a => a.status === 'syncing').length;

    return {
      pending,
      failed,
      syncing,
      total: this.syncQueue.length,
      isOnline: navigator.onLine,
      isSyncing: this.isSyncing
    };
  }

  // Retentar ações falhadas
  async retryFailed(): Promise<void> {
    const failedActions = this.syncQueue.filter(a => a.status === 'failed');
    
    failedActions.forEach(action => {
      action.status = 'pending';
      action.retries = 0;
    });
    
    if (navigator.onLine) {
      await this.syncAll();
    }
  }

  // Limpar fila de sincronização
  async clearQueue(): Promise<void> {
    const cs = await getCacheService();
    await cs.clearSyncQueue();
    this.syncQueue = [];
    console.log('🗑️ ACOR Sync: Fila de sincronização limpa');
  }

  // ✅ MÉTODOS ESPECÍFICOS PARA DIFERENTES TIPOS DE DADOS

  // ✅ ATUALIZADA: Criar tarefa offline COM RECORRÊNCIA E SUBETAPAS
  async createTarefaOffline(tarefaData: any): Promise<string> {
    const tempId = this.generateId();
    
    // ✅ VALIDAR dados de recorrência antes de cachear
    const validatedData = {
      ...tarefaData,
      temp_id: tempId,
      offline_created: true,
      id: tempId, // ID temporário para usar localmente
      // ✅ Garantir consistência dos campos de recorrência
      recorrente: tarefaData.recorrente || false,
      recorrencia_ativa: tarefaData.recorrente || false,
      padrao_recorrencia: tarefaData.recorrente ? tarefaData.padrao_recorrencia : null,
      intervalo_recorrencia: tarefaData.recorrente ? (tarefaData.intervalo_recorrencia || 1) : null,
      dias_semana: tarefaData.recorrente ? tarefaData.dias_semana : null,
      data_limite_recorrencia: tarefaData.recorrente ? tarefaData.data_limite_recorrencia : null,
      proxima_ocorrencia: null, // Será calculada pelo trigger no servidor
      tarefa_pai_id: tarefaData.tarefa_pai_id || null,
      subetapa_id: tarefaData.subetapa_id || null
    };

    // Adicionar à fila de sincronização
    await this.addToQueue({
      type: 'CREATE',
      table: 'tarefas',
      data: validatedData,
      originalId: tempId
    });

    // Adicionar ao cache local
    const cs = await getCacheService();
    await cs.cacheTarefas([validatedData]);

    console.log(`✅ ACOR Sync: Tarefa offline criada com recorrência:`, {
      id: tempId,
      recorrente: validatedData.recorrente,
      subetapa_id: validatedData.subetapa_id
    });

    return tempId;
  }

  // ✅ ATUALIZADA: Atualizar tarefa offline COM NOVOS CAMPOS
  async updateTarefaOffline(tarefaId: string, updates: any): Promise<void> {
    // ✅ VALIDAR updates de recorrência
    const validatedUpdates = { ...updates };
    
    // ✅ Se está desabilitando recorrência, limpar campos relacionados
    if (validatedUpdates.recorrente === false) {
      validatedUpdates.padrao_recorrencia = null;
      validatedUpdates.intervalo_recorrencia = null;
      validatedUpdates.dias_semana = null;
      validatedUpdates.data_limite_recorrencia = null;
      validatedUpdates.recorrencia_ativa = false;
      validatedUpdates.proxima_ocorrencia = null;
    }
    
    // ✅ Se está habilitando recorrência, garantir valores padrão
    if (validatedUpdates.recorrente === true) {
      validatedUpdates.recorrencia_ativa = true;
      if (!validatedUpdates.intervalo_recorrencia) {
        validatedUpdates.intervalo_recorrencia = 1;
      }
      if (!validatedUpdates.padrao_recorrencia) {
        validatedUpdates.padrao_recorrencia = 'daily';
      }
    }

    await this.addToQueue({
      type: 'UPDATE',
      table: 'tarefas',
      data: validatedUpdates,
      originalId: tarefaId
    });

    // Atualizar cache local
    const cs = await getCacheService();
    await cs.updateTarefaLocal(tarefaId, validatedUpdates);

    console.log(`✅ ACOR Sync: Tarefa offline atualizada:`, {
      id: tarefaId,
      updates: Object.keys(validatedUpdates)
    });
  }

  // Excluir tarefa offline (mantida igual)
  async deleteTarefaOffline(tarefaId: string): Promise<void> {
    await this.addToQueue({
      type: 'DELETE',
      table: 'tarefas',
      data: {},
      originalId: tarefaId
    });

    // Remover do cache local
    const cs = await getCacheService();
    await cs.removeTarefaLocal(tarefaId);
  }

  // ✅ NOVA: Criar subetapa offline (se necessário)
  async createSubEtapaOffline(subetapaData: any): Promise<string> {
    const tempId = this.generateId();
    const dataWithTempId = {
      ...subetapaData,
      temp_id: tempId,
      offline_created: true,
      id: tempId
    };

    await this.addToQueue({
      type: 'CREATE',
      table: 'subetapas',
      data: dataWithTempId,
      originalId: tempId
    });

    // Adicionar ao cache local
    const cs = await getCacheService();
    await cs.cacheSubEtapas([dataWithTempId]);

    return tempId;
  }

  // ✅ NOVA: Atualizar subetapa offline
  async updateSubEtapaOffline(subetapaId: string, updates: any): Promise<void> {
    await this.addToQueue({
      type: 'UPDATE',
      table: 'subetapas',
      data: updates,
      originalId: subetapaId
    });

    // ✅ TODO: Implementar updateSubEtapaLocal no cacheService se necessário
    console.log(`✅ ACOR Sync: SubEtapa ${subetapaId} atualizada offline`);
  }

  // Criar comentário offline (mantida igual)
  async createComentarioOffline(comentarioData: any): Promise<string> {
    const tempId = this.generateId();
    const dataWithTempId = {
      ...comentarioData,
      temp_id: tempId,
      offline_created: true,
      id: tempId
    };

    await this.addToQueue({
      type: 'CREATE',
      table: 'tarefas_comentarios',
      data: dataWithTempId,
      originalId: tempId
    });

    // Adicionar ao cache local
    const cs = await getCacheService();
    await cs.cacheComentarios([dataWithTempId]);

    return tempId;
  }

  // ✅ NOVA: Verificar se há tarefas recorrentes pendentes de sincronização
  async getPendingRecurrentTasks(): Promise<OfflineAction[]> {
    return this.syncQueue.filter(action => 
      action.table === 'tarefas' && 
      action.status === 'pending' &&
      action.data?.recorrente === true
    );
  }

  // ✅ NOVA: Obter estatísticas da fila de sincronização
  getSyncStatistics() {
    const byTable = this.syncQueue.reduce((acc, action) => {
      acc[action.table] = (acc[action.table] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const byType = this.syncQueue.reduce((acc, action) => {
      acc[action.type] = (acc[action.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const withRecurrence = this.syncQueue.filter(action => 
      action.table === 'tarefas' && action.data?.recorrente === true
    ).length;

    const withSubetapa = this.syncQueue.filter(action => 
      action.table === 'tarefas' && action.data?.subetapa_id
    ).length;

    return {
      byTable,
      byType,
      withRecurrence,
      withSubetapa,
      ...this.getSyncStatus()
    };
  }

  // Parar monitoramento (cleanup)
  destroy(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }
}

// Instância singleton
export const offlineSyncService = new OfflineSyncService();

// ✅ HOOK MELHORADO PARA USAR O STATUS DE SINCRONIZAÇÃO
export function useSyncStatus() {
  // Usar useState sem React import (será importado pelo componente que usa)
  const [status, setStatus] = (window as any).React?.useState?.(offlineSyncService.getSyncStatus()) || 
    [offlineSyncService.getSyncStatus(), () => {}];

  // Usar useEffect se disponível
  if ((window as any).React?.useEffect) {
    (window as any).React.useEffect(() => {
      const updateStatus = () => setStatus(offlineSyncService.getSyncStatus());
      
      offlineSyncService.addStatusListener(updateStatus);
      
      // Atualizar a cada segundo também
      const interval = setInterval(updateStatus, 1000);

      return () => {
        offlineSyncService.removeStatusListener(updateStatus);
        clearInterval(interval);
      };
    }, []);
  }

  return {
    ...status,
    retryFailed: () => offlineSyncService.retryFailed(),
    clearQueue: async () => await offlineSyncService.clearQueue(),
    forceSync: () => offlineSyncService.syncAll(),
    // ✅ NOVAS funções de estatísticas
    getStatistics: () => offlineSyncService.getSyncStatistics(),
    getPendingRecurrentTasks: () => offlineSyncService.getPendingRecurrentTasks()
  };
}